import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    Carousel as ReusableCarousel,
    Slide
} from "@/components/confluence/carousel/src";
import { ConfluenceAnchorVariant } from "@/components/signup/bxp-ui-signup/src/components";
import EmbeddedSignupModule, {
    EmbeddedSignupModuleProps
} from "@/components/signup/bxp-ui-signup/src/embedded-signup-module";
import { ClientOnly } from "@/utils/client-only";
import { FeatureExposed } from "@/utils/feature-exposed";

type PaidHeroCarouselProps = {
    embeddedSignup: EmbeddedSignupModuleProps;
    carouselSlides: Array<Slide>;
    pageId: string;
    customSignupHeader?: string;
    shouldUseTransparentBackground?: boolean;
    shouldUseBlockStartPadding?: boolean;
    shouldUseNewBrandBackground?: boolean;
};

const paidHeroContainerStyles = css({
    display: "flex",
    justifyContent: "center"
});

const backgroundGradient = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "linear-gradient(180deg, #C1EDFB 0%, #98E6FF 100%)"
});

const paidHeroContentStyles = css({
    display: "flex",
    minHeight: 750,
    maxWidth: 1272,
    columnGap: 48,
    rowGap: 24,
    width: "100%",
    justifyContent: "center"
});

const blockStartPadding = css({
    paddingBlockStart: 60
});

const signUpFormContainerStyles = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "1 1 100%",
    maxWidth: 528
});

const carouselContainerStyles = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    minWidth: 280,
    flex: "1 1 100%",
    paddingInlineEnd: 32,
    [media.below.sm]: {
        display: "none"
    }
});
const rebrandBackground = css({
    "marginBlockStart": "60px",
    "position": "relative",
    "&:before": {
        content: '""',
        position: "absolute",
        insetBlockStart: "20px",
        insetInlineStart: 0,
        insetInlineEnd: "100px",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#DEEBFF",
        insetBlockEnd: "70px",
        transform: "rotate(-1.25deg)",
        transformOrigin: "center",
        zIndex: -5,
        pointerEvents: "none"
    },
    "&:after": {
        content: '""',
        position: "absolute",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#DEEBFF",
        insetBlockStart: "63px",
        insetInlineStart: "200px",
        insetInlineEnd: 0,
        insetBlockEnd: 0,
        transform: "rotate(1deg)",
        transformOrigin: "center",
        zIndex: -5,
        pointerEvents: "none"
    }
});

export const PaidHeroCarousel = ({
    pageId,
    embeddedSignup,
    carouselSlides,
    customSignupHeader,
    shouldUseTransparentBackground,
    shouldUseBlockStartPadding = true,
    shouldUseNewBrandBackground
}: PaidHeroCarouselProps) => {
    const intl = useIntl();

    const isLiftAndShiftExposure =
        pageId === "paid-lp-conf-features" || pageId === "confluence-default";

    return (
        <FeatureExposed
            experimentKey={
                isLiftAndShiftExposure
                    ? "cfind-1466-lift-and-shift"
                    : "wac-conf-anchors-lift-and-shift"
            }
            group={isLiftAndShiftExposure ? "treatmentA" : "Test"}
        >
            <div
                id="start-trial"
                css={[
                    paidHeroContainerStyles,
                    !shouldUseTransparentBackground && backgroundGradient,
                    shouldUseNewBrandBackground && rebrandBackground
                ]}
            >
                <div
                    css={[
                        paidHeroContentStyles,
                        shouldUseBlockStartPadding && blockStartPadding
                    ]}
                >
                    <div css={signUpFormContainerStyles}>
                        <UFOSegment
                            name={
                                isLiftAndShiftExposure
                                    ? "conf-lift-and-shift-features-signup-module"
                                    : "conf-lift-and-shift-signup-module"
                            }
                        >
                            <ClientOnly>
                                <EmbeddedSignupModule
                                    {...embeddedSignup}
                                    variantOverride={
                                        pageId as ConfluenceAnchorVariant
                                    }
                                    customSignupHeader={customSignupHeader}
                                />
                            </ClientOnly>
                        </UFOSegment>
                    </div>
                    <div css={carouselContainerStyles}>
                        <UFOSegment
                            name={
                                isLiftAndShiftExposure
                                    ? "cfind-1466-carousel"
                                    : "conf-lift-and-shift-carousel"
                            }
                        >
                            <ReusableCarousel
                                aria-label={intl.formatMessage({
                                    id: "ConfluenceSoftwarePage.v4.CarouselHero.AriaLabel",
                                    defaultMessage:
                                        "A display of various confluence features",
                                    description:
                                        "Aria label for the carousel section"
                                })}
                                slides={carouselSlides}
                                id="confluence-features-carousel"
                            />
                        </UFOSegment>
                    </div>
                </div>
            </div>
        </FeatureExposed>
    );
};
